import {chain} from 'lodash';
import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {intersperseNodes} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {InboxModel} from '../../../../libs/shared/core/src/models/api/inboxModel';
import {TeammateModel} from '../../../../libs/shared/core/src/models/api/teammateModel';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {filterRenderers} from '../../../analytics/helpers/analyticsFiltersHelpers';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {AnalyticsFilterTypesEnum} from '../../../models/api/analyticsSavedViewModel';
import {getCompanyUrl} from '../../../store/data/company/companySelectors';
import {computeFilterDropdownSourceAsync} from '../../common/filters/sourceFilter/filterDropdownSource';
import {ResourceMultiSelectInput} from '../../common/resourceMultiSelectInput/resourceMultiSelectInput';
import {buildFetcher, buildRowGenerator} from './teammateFilterHelpers';
import {LensFormMultiselectProps} from './types';

/*
 * Props.
 */

interface LensFormMultiselectAssigneesProps extends LensFormMultiselectProps<TeammateModel> {
  selectedInboxIds?: ReadonlyArray<number>;
  shouldFilterTeammatesByInboxAccess?: boolean;
  selectedInboxesInState: ReadonlyArray<InboxModel>;
}

/*
 * Style.
 */

const StyledValueWrapper = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  ${ellipsis()}
`;

/*
 * Component.
 */

export const LensFormMultiSelectAssignees: FC<LensFormMultiselectAssigneesProps> = (props) => {
  const {
    isDisabled,
    choices,
    selectedIds,
    onSetItemIds,
    selectedInboxIds,
    selectedInboxesInState,
    shouldFilterTeammatesByInboxAccess,
  } = props;
  const companyUrl = useAppSelector(getCompanyUrl);
  const dropdownSource = useMemo(() => {
    const fetcher = buildFetcher(
      choices,
      selectedIds,
      companyUrl,
      selectedInboxesInState,
      selectedInboxIds,
      shouldFilterTeammatesByInboxAccess,
    );
    return computeFilterDropdownSourceAsync(fetcher);
  }, [
    choices,
    companyUrl,
    shouldFilterTeammatesByInboxAccess,
    selectedIds,
    selectedInboxIds,
    selectedInboxesInState,
  ]);

  return (
    <ResourceMultiSelectInput
      dropdownSource={dropdownSource}
      selectedItemIds={selectedIds}
      computeFilterGenerator={buildRowGenerator}
      onSetItems={onSetItemIds}
      onClearAll={() => onSetItemIds([])}
      renderValue={() => renderValue(props)}
      renderPlaceholder={() =>
        props.placeholder || <Trans id="lens-form-multiselect-assignees-placeholder">Select assignees</Trans>
      }
      label={
        props.label || (
          <Trans id="lens-form-multiselect-assignees-label">Filtered by any of these assignees</Trans>
        )
      }
      dropdownTitle={<Trans id="lens-form-multiselect-assignees-dropdown-title">Teammates</Trans>}
      isDisabled={isDisabled}
      isRequired={props.isRequired}
      messageForDisabled={
        <Trans id="lens-form-multiselect-missing-inbox-message">
          Field will be enabled once an inbox is selected.
        </Trans>
      }
      hasError={props.hasError}
      errorMessage={props.errorMessage}
    />
  );
};

/*
 * Helpers.
 */

function renderValue(props: LensFormMultiselectAssigneesProps) {
  const {selectedIds, choices} = props;

  if (!selectedIds.length) {
    return null;
  }

  const selectedTeammates = chain(selectedIds)
    .map((id) => choices.find((teammate) => teammate.id === id))
    .compact()
    .value();

  return (
    <StyledValueWrapper>
      {intersperseNodes(
        selectedTeammates.map((teammate) => ({
          node: filterRenderers[AnalyticsFilterTypesEnum.TEAMMATES].renderItem(teammate),
          key: String(teammate.id),
        })),
        () => ', ',
      )}
    </StyledValueWrapper>
  );
}
